import { UpcomingService, UpcomingServiceUpdate } from "@models/UpcomingService";
import { convertIdInArray, convertIdInObject } from "@util/convert-id-field";
import createFormData from "@util/create-form.data";
import { transformUpcomingService } from "@util/body-parse";
import { AxiosProgressEvent } from "axios";
import apiClient from "./apiClient";

interface UpcomingServiceWithId extends Omit<UpcomingService, "id"> {
    _id: string;
}

export const fetchUpcomingServices = async (): Promise<UpcomingService[]> => {
    try {
        const response = await apiClient.get<UpcomingServiceWithId[]>("/upcoming-service");
        const servicesWithIds = convertIdInArray(response.data);
        // Transform each service to parse date fields correctly
        return servicesWithIds.map(transformUpcomingService);
    } catch (error) {
        console.error("Failed to fetch funeral notices", error);
        throw error;
    }
};

export const fetchUpcomingService = async (id: string): Promise<UpcomingService> => {
    try {
        const response = await apiClient.get<UpcomingServiceWithId>(`/upcoming-service/${id}`);
        const serviceWithId = convertIdInObject(response.data);

        // Transform the service to parse date fields correctly
        return transformUpcomingService(serviceWithId);
    } catch (error) {
        console.error("Failed to fetch funeral notices", error);
        throw error;
    }
};

export const createUpcomingService = async (
    service: UpcomingServiceUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<string> => {
    try {
        const formData = createFormData(service);

        const response = await apiClient.post("/upcoming-service", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });

        return response.data._id;
    } catch (error) {
        console.error("Failed to create funeral notices", error);
        throw error;
    }
};

export const updateUpcomingService = async (
    id: string,
    updates: UpcomingServiceUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
)
: Promise<void> => {
    try {
        const formData = createFormData(updates);

        await apiClient.patch(`/upcoming-service/${id}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });
    } catch (error) {
        console.error("Failed to update funeral notices", error);
        throw error;
    }
};

export const deleteUpcomingService = async (id: string): Promise<void> => {
    try {
        await apiClient.patch(`/upcoming-service/${id}/delete`);
    } catch (error) {
        console.error("Failed to delete funeral notices", error);
        throw error;
    }
};
